import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader, CheckCircle } from 'lucide-react';
import { apiCall } from '../api';
import './CreateWorkOrder.css';

const REPORT_TYPES = [
  'SIP APPLICATION FORM (BAJAJ)'
];

const CreateWorkOrder = () => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [filePreviews, setFilePreviews] = useState({});
  const [formData, setFormData] = useState({
    reportType: REPORT_TYPES[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  
  // Use ref to track previews for cleanup
  const filePreviewsRef = useRef({});

  // Update ref when filePreviews changes
  useEffect(() => {
    filePreviewsRef.current = filePreviews;
  }, [filePreviews]);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all object URLs to prevent memory leaks
      Object.values(filePreviewsRef.current).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      // Revoke old URLs before creating new ones
      Object.values(filePreviews).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      setSelectedFiles(files);
      setFileUploaded(true);
      
      const newPreviews = {};
      files.forEach((file, idx) => {
        setUploadProgress(prev => ({ ...prev, [idx]: 0 }));
        // Create object URL for immediate preview
        newPreviews[idx] = URL.createObjectURL(file);
      });
      setFilePreviews(newPreviews);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      // Revoke old URLs before creating new ones
      Object.values(filePreviews).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      setSelectedFiles(files);
      setFileUploaded(true);
      
      const newPreviews = {};
      files.forEach((file, idx) => {
        setUploadProgress(prev => ({ ...prev, [idx]: 0 }));
        // Create object URL for immediate preview
        newPreviews[idx] = URL.createObjectURL(file);
      });
      setFilePreviews(newPreviews);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRemoveFile = (index) => {
    // Revoke the URL for the removed file
    if (filePreviews[index] && filePreviews[index].startsWith('blob:')) {
      URL.revokeObjectURL(filePreviews[index]);
    }
    
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    if (newFiles.length === 0) {
      setFileUploaded(false);
      setFilePreviews({});
      setUploadProgress({});
    } else {
      // Rebuild previews with new indices
      const newPreviews = {};
      const newProgress = {};
      newFiles.forEach((file, idx) => {
        newPreviews[idx] = URL.createObjectURL(file);
        newProgress[idx] = 0;
      });
      setFilePreviews(newPreviews);
      setUploadProgress(newProgress);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      setError('Please upload at least one file');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const traceId = `TD-${Math.floor(Date.now() / 1000)}`;
      const woData = await apiCall('/api/work-orders', {
        method: 'POST',
        body: JSON.stringify({
          reportType: formData.reportType,
          customerName: 'Unknown',
          associatedParty: '',
          traceId: traceId
        })
      });
      const workOrderId = woData._id;

      let uploadedCount = 0;
      const token = localStorage.getItem('token');

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const fileFormData = new FormData();
        fileFormData.append('file', file);

        try {
          setUploadProgress(prev => ({ ...prev, [i]: 50 }));

          const fileResponse = await apiCall(`/api/work-orders/${workOrderId}/files`, {
            method: 'POST',
            body: fileFormData,

          });

          if (fileResponse) {
            uploadedCount++;
            setUploadProgress(prev => ({ ...prev, [i]: 100 }));
          }
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          setUploadProgress(prev => ({ ...prev, [i]: 100 }));
        }
      }

      // Trigger extraction automatically if files were uploaded
      if (uploadedCount > 0) {
        console.log(`[CreateWO] Starting automatic extraction for ${uploadedCount} files...`);

        // Trigger extraction immediately (no delay)
        try {
          console.log(`[CreateWO] Triggering extraction immediately...`);
          const extractResponse = await fetch(`/api/work-orders/${workOrderId}/extract`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (extractResponse.ok || extractResponse.status === 202) {
            console.log(`[CreateWO] ✅ Extraction triggered successfully`);
          } else {
            console.error(`[CreateWO] ❌ Extraction trigger failed:`, extractResponse.status);
          }
        } catch (err) {
          console.error(`[CreateWO] ❌ Error triggering extraction:`, err);
        }
      }

      // Navigate to work order detail page
      navigate(`/work-order/${workOrderId}`, {
        state: { uploadedCount, totalFiles: selectedFiles.length }
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="create-work-order">
      <div className="form-wrapper">
        <div className="page-title-line">
          <span>Upload documents to create a new work order</span>
        </div>

        <div className="form-container split-layout">
          {error && (
            <div className="error-box">
              <span>{error}</span>
              <button onClick={() => setError('')} className="close-btn">✕</button>
            </div>
          )}

          <div className="split-wrapper">
            {/* Left Section: Form & Upload */}
            <div className="split-section left-section">
              <form onSubmit={handleSubmit} className="form-content">
                {/* Report Type */}
                <div className="form-group">
                  <label>Report Type *</label>
                  <select
                    name="reportType"
                    value={formData.reportType}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    {REPORT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* File Upload */}
                <div className="form-group file-upload-group">
                  <label>Upload Files *</label>
                  <div
                    className={`file-upload ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileSelect}
                      id="fileInput"
                      disabled={loading}
                    />
                    <label htmlFor="fileInput" className="file-label">
                      <Upload size={40} className="upload-icon" />
                      <p className="upload-text">Click to select or drag & drop files here</p>
                      <p className="upload-hint">Supports PDF files</p>
                    </label>
                  </div>
                  {selectedFiles.length > 0 && (
                    <div className="selected-files-list">
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} className="selected-file-item">
                          <FileText size={16} />
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                          <button 
                            type="button" 
                            className="remove-file-btn"
                            onClick={() => handleRemoveFile(idx)}
                            disabled={loading}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => navigate('/work-orders')}
                    className="btn-cancel"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || selectedFiles.length === 0}
                    className={`btn-create ${fileUploaded && Object.keys(filePreviews).length > 0 ? 'btn-create-ready' : ''}`}
                  >
                    {loading ? (
                      <>
                        <Loader size={16} className="spinner" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Create
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Section: PDF Preview */}
            <div className="split-section right-section">
              <div className="preview-header">
                <FileText size={20} />
                <span>Document Preview</span>
              </div>
              <div className="preview-container">
                {selectedFiles.length === 0 ? (
                  <div className="preview-empty">
                    <FileText size={48} />
                    <p>Upload files to preview</p>
                  </div>
                ) : (
                  <div className="preview-scroll-area">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="preview-wrapper">
                        <div className="pdf-filename">
                          <FileText size={18} />
                          {file.name} - Review
                        </div>
                        <div className="pdf-section-label">📄 Document</div>
                        <div className="pdf-viewer-area">
                          {filePreviews[idx] ? (
                            <embed
                              src={`${filePreviews[idx]}#view=FitH&scrollbar=1&toolbar=0&navpanes=0`}
                              type="application/pdf"
                              className="pdf-viewer"
                            />
                          ) : (
                            <div className="pdf-loading">
                              <Loader size={32} className="spinner" />
                              <p>Loading PDF...</p>
                            </div>
                          )}
                        </div>
                        <div className="pdf-footer">
                          <div className="pdf-info">
                            <span className="pdf-name">{file.name}</span>
                            <span className="pdf-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          {uploadProgress[idx] !== undefined && (
                            <div className="pdf-progress">
                              {uploadProgress[idx] < 100 ? (
                                <>
                                  <div className="progress-bar">
                                    <div
                                      className="progress-fill"
                                      style={{ width: `${uploadProgress[idx]}%` }}
                                    ></div>
                                  </div>
                                  <span>{uploadProgress[idx]}%</span>
                                </>
                              ) : (
                                <span className="progress-done">✓</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkOrder;
